'use memo';

import { useCallback, useContext } from 'react';
import {
  AgxPropertyAddressForm,
  PropertyAddress,
} from '@urbanx/agx-ui-components';
import {
  DevCampaignScenarioCreateValues,
  DevCampaignScenarioCreatedCampaign,
  DevCampaignScenarioDialog,
} from '@urbanx/agx.ui-components2';
import { AuthContext } from '@urbanx/agx.auth.client';
import {
  ActivitiesApiContext,
  TaskProgressorRequestMode,
  TaskProgressorRequestTargetStage,
  useProgressTasksWithTaskProgressor,
} from '@urbanx/agx.activities.client';
import {
  type AustralianAddress,
  DevCreateContractTestCampaignRequestContractCreationSource,
  DevCreateContractTestCampaignRequestContractStage,
  DevCreateContractTestCampaignRequestCreateFrom,
  DevCreateContractTestCampaignRequestState,
  useDevCreateContractTestCampaign,
} from '@urbanx/agx.campaigns.client';
import { fetchAddresses, fetchPropertyDetails } from 'Api/RpData/rpDataApi';
import { PropertySuggestionItem } from 'Api/RpData/Types/types';
import {
  reloadCampaigns,
  selectCampaign,
} from 'features/campaigns/campaignsReducer';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useSuburbSuggestions from 'hooks/useSuburbSuggestions';

interface DevCampaignScenarioModalProps {
  onClose: () => void;
}

interface ManualAddressInputProps {
  state: DevCreateContractTestCampaignRequestState;
  disabled: boolean;
  value: AustralianAddress;
  onChange: (value: AustralianAddress) => void;
  onValidityChange: (isValid: boolean) => void;
}

const getStaffPortalCampaignUrl = ({
  campaignId,
  agencyId,
}: DevCampaignScenarioCreatedCampaign) => {
  const staffPortalOrigin = ['localhost', '127.0.0.1'].includes(
    window.location.hostname
  )
    ? `${window.location.protocol}//${window.location.hostname}:3001`
    : window.location.origin;

  return `${staffPortalOrigin}/agencies/${agencyId}/campaigns/${campaignId}/campaign-tasks`;
};

const ManualAddressInput = ({
  state,
  disabled,
  value,
  onChange,
  onValidityChange,
}: ManualAddressInputProps) => {
  const { suburbSuggestions, getSuburbSuggestions } =
    useSuburbSuggestions(state);

  return (
    <AgxPropertyAddressForm
      key={`dev-campaign-scenario-manual-address-${state}`}
      id="dev-campaign-scenario-manual-address"
      searchSuburb
      hideUnitLot
      showSearchForAddress={false}
      propertyAddressField={false}
      suburbSuggestions={suburbSuggestions}
      onFetchSuburbSuggestions={suburbTerm =>
        getSuburbSuggestions(suburbTerm || '')
      }
      onClearSuburbSuggestions={() => getSuburbSuggestions('')}
      onDisableNextButton={disabled => onValidityChange(!disabled)}
      disabled={disabled}
      required
      defaultValue={value as PropertyAddress}
      onAddressSelected={selectedAddress => {
        if (selectedAddress) onChange(selectedAddress);
      }}
    />
  );
};

export const DevCampaignScenarioModal = ({
  onClose,
}: DevCampaignScenarioModalProps) => {
  const { getAuthToken } = useContext(AuthContext);
  const activitiesApi = useContext(ActivitiesApiContext);
  const dispatch = useAppDispatch();
  const { devCreateContractTestCampaign } = useDevCreateContractTestCampaign({
    onSuccess: () => {},
    onError: () => {},
  });
  const { progressTasksWithTaskProgressor } =
    useProgressTasksWithTaskProgressor({
      onSuccess: () => {},
      onError: () => {},
    });

  const selectFirstSuggestion = useCallback(
    async (
      authToken: string,
      query: string,
      state: DevCreateContractTestCampaignRequestState
    ): Promise<PropertySuggestionItem | null> => {
      const abortController = new AbortController();
      const response = await fetchAddresses(
        authToken,
        query,
        state,
        abortController.signal
      );

      return response?.suggestions?.[0] ?? null;
    },
    []
  );

  const createFromRpData = useCallback(
    async (authToken: string, values: DevCampaignScenarioCreateValues) => {
      const suggestion = await selectFirstSuggestion(
        authToken,
        values.rpDataQuery,
        values.state
      );
      const propertyId = suggestion?.suggestionProperties?.[0]?.propertyId;

      if (!propertyId)
        throw new Error('No RPData property was found for that search.');

      const allPropertyDetails = await fetchPropertyDetails(
        authToken,
        propertyId
      );

      if (!allPropertyDetails)
        throw new Error('RPData did not return property details.');

      return await devCreateContractTestCampaign({
        createFrom: DevCreateContractTestCampaignRequestCreateFrom.RPDataSearch,
        state: values.state,
        propertyAddress: allPropertyDetails.matchedAddress,
        propertyDetails: allPropertyDetails.propertyDetails,
        propertyImageUrls: allPropertyDetails.propertyImageUrls,
        hasPool: allPropertyDetails.hasPool,
        agreementSource: values.agreementSource,
        agreementDocuSignSigned: values.agreementDocuSignSigned,
        listingLaunch: values.listingLaunch,
        contract: values.contract,
        contractCreationSource:
          DevCreateContractTestCampaignRequestContractCreationSource.AgentPortal,
        contractConditions: values.conditions,
        contractStage: values.contractStage,
        depositProfile: values.depositProfile,
      });
    },
    [devCreateContractTestCampaign, selectFirstSuggestion]
  );

  const handleCreate = useCallback(
    async (values: DevCampaignScenarioCreateValues) => {
      if (
        values.createFrom ===
        DevCreateContractTestCampaignRequestCreateFrom.RPDataSearch
      ) {
        const authToken = await getAuthToken?.();
        if (!authToken)
          throw new Error('Unable to authenticate dev seeder call.');

        return createFromRpData(authToken, values);
      }

      return await devCreateContractTestCampaign({
        createFrom:
          DevCreateContractTestCampaignRequestCreateFrom.ManualAddress,
        state: values.state,
        propertyAddress: {
          ...values.manualAddress,
          state: values.manualAddress.state || values.state,
        } as PropertyAddress,
        agreementSource: values.agreementSource,
        agreementDocuSignSigned: values.agreementDocuSignSigned,
        listingLaunch: values.listingLaunch,
        contract: values.contract,
        contractCreationSource:
          DevCreateContractTestCampaignRequestContractCreationSource.AgentPortal,
        contractConditions: values.conditions,
        contractStage: values.contractStage,
        depositProfile: values.depositProfile,
      });
    },
    [createFromRpData, devCreateContractTestCampaign, getAuthToken]
  );

  const handleProgressContractWorkflow = useCallback(
    async (
      campaign: DevCampaignScenarioCreatedCampaign,
      status: DevCreateContractTestCampaignRequestContractStage
    ) => {
      const progressWorkflow = async (
        targetStage: TaskProgressorRequestTargetStage,
        contractId?: string
      ) => {
        const details = await activitiesApi.getTaskProgressorCampaignDetails(
          campaign.campaignId
        );

        if (!details.agencyId || !details.campaignVersion) {
          throw new Error(
            'Task progressor returned incomplete campaign details.'
          );
        }

        await progressTasksWithTaskProgressor({
          agencyId: details.agencyId,
          campaignId: campaign.campaignId,
          contractId,
          targetStage,
          mode: TaskProgressorRequestMode.ChangeStage,
          campaignVersion: details.campaignVersion,
        });
      };

      await progressWorkflow(TaskProgressorRequestTargetStage.Launch);

      if (!campaign.contractId) return;

      switch (status) {
        case DevCreateContractTestCampaignRequestContractStage.NewContract:
          return;
        case DevCreateContractTestCampaignRequestContractStage.Accepted:
          await progressWorkflow(
            TaskProgressorRequestTargetStage.NewContract,
            campaign.contractId
          );
          return;
        case DevCreateContractTestCampaignRequestContractStage.Unconditional:
          await progressWorkflow(
            TaskProgressorRequestTargetStage.Unconditional,
            campaign.contractId
          );
      }
    },
    [activitiesApi, progressTasksWithTaskProgressor]
  );

  const handleCreated = useCallback(
    (campaign: DevCampaignScenarioCreatedCampaign) => {
      dispatch(reloadCampaigns());
      dispatch(selectCampaign(campaign.campaignId));
    },
    [dispatch]
  );

  return (
    <DevCampaignScenarioDialog
      onClose={onClose}
      onCreate={handleCreate}
      onProgressContractWorkflow={handleProgressContractWorkflow}
      onCreated={handleCreated}
      getStaffPortalUrl={getStaffPortalCampaignUrl}
      renderManualAddressForm={props => <ManualAddressInput {...props} />}
    />
  );
};

export default DevCampaignScenarioModal;
